import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../../api/auth";
import { COUNTRY_OPTIONS } from "../../shared/countries";
import { errorMessage } from "../../shared/errors";
import { useInvalidateMe } from "../../shared/hooks/useMe";
import { navigateAfterAuth } from "../../shared/returnTo";
import { Turnstile } from "../../shared/Turnstile";
import { Alert, Button, Input } from "../../ui";

export function RegisterForm({
  returnTo,
  onSwitch,
}: {
  returnTo: string | null;
  onSwitch?: () => void;
}) {
  const navigate = useNavigate();
  const invalidateMe = useInvalidateMe();
  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    country: "",
  });
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const onToken = useCallback((token: string | null) => setCaptcha(token), []);

  useEffect(() => {
    authApi
      .context()
      .then((ctx) => {
        if (ctx.country)
          setForm((f) =>
            f.country ? f : { ...f, country: ctx.country as string },
          );
      })
      .catch(() => undefined);
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.register({
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name || undefined,
        country: form.country || undefined,
        turnstile_token: captcha ?? undefined,
        return_to: returnTo ?? undefined,
      });
      await invalidateMe();
      navigateAfterAuth(result.return_to ?? returnTo, navigate);
    } catch (err) {
      setError(errorMessage(err, "Registration failed."));
    } finally {
      setBusy(false);
    }
  };
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value });

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4"
      aria-label="Create account"
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <Input
        label="Email"
        type="email"
        autoComplete="email"
        required
        value={form.email}
        onChange={set("email")}
      />
      <Input
        label="Password"
        type="password"
        autoComplete="new-password"
        required
        minLength={10}
        value={form.password}
        onChange={set("password")}
      />
      <Input
        label="First name"
        autoComplete="given-name"
        required
        value={form.first_name}
        onChange={set("first_name")}
      />
      <Input
        label="Last name"
        autoComplete="family-name"
        value={form.last_name}
        onChange={set("last_name")}
      />
      <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
        Country
        <select
          className="rounded-md border border-slate-300 px-3 py-2 text-sm font-normal"
          value={form.country}
          onChange={set("country")}
        >
          <option value="">Select a country</option>
          {COUNTRY_OPTIONS.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <Turnstile onToken={onToken} />
      <Button type="submit" loading={busy}>
        Create account
      </Button>
      {onSwitch ? (
        <button
          type="button"
          className="text-sm text-brand underline"
          onClick={onSwitch}
        >
          I already have an account
        </button>
      ) : null}
    </form>
  );
}
